import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { Navigate, useLocation } from 'react-router-dom';
import { useSimpleAuth } from '../../contexts/SimpleAuthContext';
import LoadingSpinner from './LoadingSpinner';
import {
  safeDecodeJwtPayload,
  clearAuthSessionStorage,
  notifyAuthSessionChanged,
  isJwtExpired,
} from '../../utils/authUtils';

const rejectClientSession = (reason) => {
  clearAuthSessionStorage();
  notifyAuthSessionChanged(reason);
};

const ClientProtectedRoute = ({ children }) => {
  const { isLoading } = useSimpleAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkClientAuth = async () => {
      try {
        // ตรวจสอบว่าไม่ใช่ admin (admin ไม่ควรเข้า client dashboard)
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
          console.log('❌ Admin cannot access client dashboard');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // ตรวจสอบ client token และ user (รองรับทั้ง localStorage และ sessionStorage)
        const token = localStorage.getItem('authToken') || localStorage.getItem('fitconnect_token');
        const userData = localStorage.getItem('authUser') || localStorage.getItem('fitconnect_user')
          || sessionStorage.getItem('authUser') || sessionStorage.getItem('fitconnect_user');
        
        console.log('🔍 ClientProtectedRoute checking credentials:', { 
          hasToken: !!token, 
          hasUserData: !!userData,
          pathname: location.pathname 
        });
        
        if (!token || !userData) {
          console.log('❌ No client credentials found (token:', !!token, ', userData:', !!userData, ')');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // Parse user data เพื่อตรวจสอบ role
        let user;
        try {
          user = JSON.parse(userData);
        } catch (e) {
          console.log('❌ Invalid user data format');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // ตรวจสอบว่าเป็น client หรือไม่
        const userRole = (user.role || '').toLowerCase();
        const isClient = userRole === 'client' || 
                        userRole === 'customer' ||  // เพิ่ม customer role
                        userRole === 'member' || 
                        userRole === 'user' || 
                        userRole === 'ลูกค้า' || 
                        userRole === 'สมาชิก';

        if (!isClient) {
          console.log('❌ User is not a client, role is:', userRole);
          setChecking(false);
          setIsAuthorized(false);
          return;
        }
        
        console.log('✅ User is a client, role:', userRole);

        // ตรวจสอบ token expiration (เฉพาะ JWT token) - ใช้ safe decode เพื่อไม่ให้ atob throw
        if (isJwtExpired(token)) {
          console.log('❌ Client token expired');
          rejectClientSession('client_token_expired');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        const tokenData = safeDecodeJwtPayload(token);
        if (tokenData?.exp) {
          console.log('✅ JWT token is valid');
        } else if (token && token.split('.').length === 3) {
          console.log('⚠️ Non-JWT or malformed payload, skipping expiration check');
        }

        // ตรวจสอบกับ server (optional - สำหรับความปลอดภัยเพิ่มเติม)
        try {
          console.log('🔍 Verifying client token with server...');
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const baseUrl = API_CONFIG.BASE_URL || (process.env.NODE_ENV === 'production' ? 'https://www.ptthaiplus.com/ptthaiplus_app/api' : 'http://localhost:3001/api');
          const response = await fetch(`${baseUrl}/auth/verify`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          if (!response.ok) {
            // เฉพาะ 401 (ไม่มีสิทธิ์/ token หมดอายุ) ให้ปฏิเสธและล้าง credentials
            if (response.status === 401) {
              console.log('❌ Token invalid or expired (401)');
              rejectClientSession('client_verify_401');
              setChecking(false);
              setIsAuthorized(false);
              return;
            }
            // 500 หรือ server error อื่น ให้ถือว่า client-side ผ่านแล้วอนุญาตเข้า
            console.log('⚠️ Server verify error', response.status, ', using client-side validation');
            setIsAuthorized(true);
            setChecking(false);
            return;
          }

          const data = await response.json();
          console.log('✅ Client authentication verified:', data);
          
          // หมายเหตุ: ไม่ใช้ role จาก server ในการบล็อกแล้ว
          // ใช้เฉพาะสำหรับยืนยันว่า token ยังใช้ได้อยู่
          // เพื่อหลีกเลี่ยงกรณี server เก็บ role คนละแบบกับ frontend (เช่น trainer/customer)

          setIsAuthorized(true);
          setChecking(false);
        } catch (error) {
          console.log('⚠️ Client token verification failed:', error.message);
          // Network/timeout/parse error → อนุญาตเข้าโดยใช้ client-side validation
          console.log('🌐 Using client-side validation');
          setIsAuthorized(true);
          setChecking(false);
        }
      } catch (error) {
        console.error('Error checking client auth:', error);
        setChecking(false);
        setIsAuthorized(false);
      }
    };

    checkClientAuth();
  }, [location.pathname]);

  // แสดง loading ขณะตรวจสอบ
  if (isLoading || checking) {
    return <LoadingSpinner fullScreen text="กำลังตรวจสอบสิทธิ์ลูกค้า..." />;
  }

  // ถ้าไม่มีสิทธิ์ ให้ redirect ไปหน้า login
  if (!isAuthorized) {
    // ตรวจสอบว่ามี admin token หรือ trainer role หรือไม่ เพื่อแสดง message ที่เหมาะสม
    const adminToken = localStorage.getItem('adminToken');
    let message = 'กรุณาเข้าสู่ระบบด้วยบัญชีลูกค้า';
    
    if (adminToken) {
      message = 'แอดมินไม่สามารถเข้าถึงหน้าลูกค้าได้';
    } else {
      const userData = localStorage.getItem('authUser') || localStorage.getItem('fitconnect_user')
        || sessionStorage.getItem('authUser') || sessionStorage.getItem('fitconnect_user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          const userRole = (user.role || '').toLowerCase();
          if (userRole === 'trainer' || userRole === 'เทรนเนอร์') {
            message = 'เทรนเนอร์ไม่สามารถเข้าถึงหน้าลูกค้าได้';
          }
        } catch (e) {
          // ignore parse error
        }
      }
    }

    console.log('🚫 Not authorized - redirecting to signin');
    return (
      <Navigate 
        to="/signin" 
        state={{ 
          from: location.pathname,
          message: message
        }} 
        replace 
      />
    );
  }

  // ถ้าผ่านการตรวจสอบแล้ว ให้แสดงหน้าลูกค้า
  return children;
};

export default ClientProtectedRoute;

